import { z } from "zod";

import { type MessageRequestUnauthorized, makeMessageRequestUnauthorized } from "../api-types/auth";
import { type Env } from "../http/Env";
import { response } from "../metadata-http";
import { graphqlRequest } from "../moment-graphql-api/graphqlRequest";

export enum Permission {
	READ = "READ",
	WRITE = "WRITE",
	EXECUTE = "EXECUTE",
	SHARE = "SHARE",
	DELETE = "DELETE",
	PIN = "PIN",
	CREATE_TEMPLATE = "CREATE_TEMPLATE",
	COMMENT_READ = "COMMENT_READ",
	COMMENT_WRITE = "COMMENT_WRITE",
	COMMENT_MANAGE = "COMMENT_MANAGE",
}

export type AuthorizationInput = z.infer<typeof authorizationInput>;
export const authorizationInput = z.object({
	canvasID: z.string(),
	permissionNeeded: z.string(),
});
export type AuthenticationResponse = z.infer<typeof authorizationResponsePayload>;
export const authorizationResponsePayload = z.object({
	error: z.optional(z.string()),
	hasPermission: z.boolean(),
	canvasID: z.string(),
	userID: z.string(),
	userPermissions: z.array(z.string()),
});
export const messageWriteAuthenticationResponse = z.object({
	authorized: authorizationResponsePayload,
});

export interface AuthorizationRequestOptions {
	env: Env;
	fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
	authorizationHeader: string | undefined;
	authorizationInput: AuthorizationInput;
}

export async function isAuthorized(
	options: AuthorizationRequestOptions
): Promise<AuthenticationResponse> {
	const authorizationInput = options.authorizationInput;
	const authResponse = await graphqlRequest({
		env: options.env,
		fetch: options.fetch,
		authorizationHeader: options.authorizationHeader,
		query: authorizationQuery(authorizationInput),
		responseSchema: messageWriteAuthenticationResponse,
	});
	if (authResponse.authorized.hasPermission) {
		return makeMessageAuthenticationSuccess(
			authorizationInput.canvasID,
			authResponse.authorized.userID,
			authResponse.authorized.userPermissions
		);
	}
	return makeMessageAuthenticationRejected(
		authorizationInput.canvasID,
		authResponse.authorized.userID,
		authResponse.authorized.userPermissions
	);
}

export function makeMessageAuthenticationRejected(
	canvasID: string,
	userID: string,
	userPermissions: string[] = []
) {
	return {
		error: "no-user-permissions",
		hasPermission: false,
		canvasID,
		userID,
		userPermissions: userPermissions.map((str) => Permission[str]),
	};
}

export function makeMessageAuthenticationSuccess(
	canvasID: string,
	userID: string,
	userPermissions: string[]
) {
	return {
		hasPermission: true,
		canvasID,
		userID,
		userPermissions: userPermissions.map((str) => Permission[str]),
	};
}

const authorizationQuery = (authorizationInput: AuthorizationInput) => ({
	operationName: "Authorization",
	query: `
	query Authorization($input: AuthorizeInput!) {
	  authorized(input: $input) {
		canvasID
		hasPermission
		userID
		userPermissions
	  }
	}`,
	variables: {
		input: {
			canvasID: authorizationInput.canvasID,
			permissionNeeded: authorizationInput.permissionNeeded,
		},
	},
});

export const authRejectedResponse = (version: number) => {
	return response<MessageRequestUnauthorized>(401, makeMessageRequestUnauthorized(version));
};
